import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import html2canvas from "html2canvas";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { loadEmployeesForBillingBranch } from "../helpers/branchEmployeeDirectory";
import {
  buildStaffLineWhatsAppText,
  buildStaffPoolPctWhatsAppText,
  parseStaffLineCuts,
  sanitizePhoneForWhatsApp,
  staffLineWhatsAppKey,
  WHATSAPP_BLUE_TICK_PATHS,
  WHATSAPP_ICON_PATH,
  whatsAppSendUrl,
} from "../helpers/monthlyIncentiveWhatsApp";
import { resolveStaffPoolPctOverridesFromSnapshot } from "../config/employeeStaffPoolPctPlans";
import {
  saveAccountantPhone as saveAccountantPhoneCloud,
  subscribeIncentiveConfig,
} from "../services/monthlyIncentiveConfigService";
import {
  formatFinancialYearLabel,
  getCalendarMonthFromFySlot,
  getFinancialYearDropdownOptions,
  getFinancialYearMonthSequence,
} from "../utils/financialYear";
import {
  monthlySaleReportQuery,
  readMonthlySaleReportParams,
} from "../utils/monthlySaleReportNav";
import {
  loadMonthlyIncentiveSnapshot,
  markStaffLineWhatsAppSent,
  normalizePersonTotalsFromFirestore,
} from "../services/monthlyIncentiveSnapshotService";
import "./MonthlySaleReportPage.css";

const LS_ACCOUNTANT_PHONE = "monthlyIncentiveAccountantPhone_v1";

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const BRANCH_OPTIONS = [
  { value: "Main", label: "Main Branch" },
  { value: "Branch 2", label: "Spareway" },
  { value: "Branch 3", label: "Branch" },
];

function fmt(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v));
}

function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  if (n <= 0) return "—";
  return `${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n)}%`;
}

export default function MonthlyIncentiveViewPage() {
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = readMonthlySaleReportParams(searchParams, now);

  const [fyStartYear, setFyStartYear] = useState(initial.fyStartYear);
  const [fySlot, setFySlot] = useState(initial.fySlot);
  const [branchFilter, setBranchFilter] = useState(initial.branchFilter);

  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [accountantPhoneRaw, setAccountantPhoneRaw] = useState("");
  const staffAllocationImageCardRef = useRef(null);
  const [staffAllocationImageBusy, setStaffAllocationImageBusy] = useState(false);
  const [staffAllocationImageUrl, setStaffAllocationImageUrl] = useState("");
  /** @type {[Record<string, string>, Function]} lineKey → ISO sentAt */
  const [whatsappSentStaffLines, setWhatsappSentStaffLines] = useState({});
  const [whatsappMarkingKey, setWhatsappMarkingKey] = useState("");

  const { calendarYear: year, monthIndex } = getCalendarMonthFromFySlot(fyStartYear, fySlot);
  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const fySelectOptions = useMemo(() => getFinancialYearDropdownOptions(fyStartYear), [fyStartYear]);
  const fyMonthSequence = useMemo(() => getFinancialYearMonthSequence(fyStartYear), [fyStartYear]);
  const branchLabel = BRANCH_OPTIONS.find((b) => b.value === branchFilter)?.label ?? branchFilter;
  const periodLabel = `${MONTH_OPTIONS[monthIndex]} ${year}`;

  const editHref = `/reports/monthly-incentive?${monthlySaleReportQuery(fyStartYear, fySlot, branchFilter)}`;

  useEffect(() => {
    setSearchParams(monthlySaleReportQuery(fyStartYear, fySlot, branchFilter), { replace: true });
  }, [fyStartYear, fySlot, branchFilter, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await loadMonthlyIncentiveSnapshot({ year, monthIndex, branchFilter });
        if (!cancelled) {
          setSaved(data);
          const sent = data?.whatsappSentStaffLines;
          setWhatsappSentStaffLines(
            sent && typeof sent === "object" && !Array.isArray(sent) ? { ...sent } : {}
          );
        }
      } catch (e) {
        if (!cancelled) {
          setSaved(null);
          setWhatsappSentStaffLines({});
          toast.error(e?.message || String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, monthIndex, branchFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setEmployeesLoading(true);
      setEmployees([]);
      try {
        const list = await loadEmployeesForBillingBranch(branchFilter);
        if (!cancelled) setEmployees(list);
      } catch (e) {
        if (!cancelled) {
          console.error("[monthlyIncentiveView] employees load failed", e);
          setEmployees([]);
        }
      } finally {
        if (!cancelled) setEmployeesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchFilter]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_ACCOUNTANT_PHONE);
      const all = raw ? JSON.parse(raw) : {};
      const v = all && typeof all === "object" ? all[branchFilter] : "";
      setAccountantPhoneRaw(v != null ? String(v) : "");
    } catch {
      setAccountantPhoneRaw("");
    }

    const unsub = subscribeIncentiveConfig(
      branchFilter,
      (remote) => {
        if (remote?.accountantPhone == null) return;
        const phone = String(remote.accountantPhone);
        setAccountantPhoneRaw(phone);
        try {
          const raw = localStorage.getItem(LS_ACCOUNTANT_PHONE);
          const all = raw ? JSON.parse(raw) : {};
          const next = all && typeof all === "object" ? { ...all } : {};
          next[branchFilter] = phone;
          localStorage.setItem(LS_ACCOUNTANT_PHONE, JSON.stringify(next));
        } catch {
          /* ignore local cache failure */
        }
      },
      () => {}
    );
    return () => {
      try {
        unsub?.();
      } catch {
        /* ignore */
      }
    };
  }, [branchFilter]);

  const accountantPhoneDigits = useMemo(
    () => sanitizePhoneForWhatsApp(accountantPhoneRaw),
    [accountantPhoneRaw]
  );

  const saveAccountantPhone = useCallback(async () => {
    try {
      const raw = localStorage.getItem(LS_ACCOUNTANT_PHONE);
      const all = raw ? JSON.parse(raw) : {};
      const next = all && typeof all === "object" ? { ...all } : {};
      next[branchFilter] = accountantPhoneRaw;
      localStorage.setItem(LS_ACCOUNTANT_PHONE, JSON.stringify(next));
    } catch {
      /* local cache failure should not block cloud save */
    }
    const tId = toast.loading("Saving accountant number…");
    try {
      await saveAccountantPhoneCloud(branchFilter, accountantPhoneRaw);
      toast.success("Accountant number saved. Synced to all PCs.", { id: tId });
    } catch (e) {
      console.error("[monthlyIncentiveView] accountant phone cloud save failed", e);
      toast.error(`Saved on this PC but cloud sync failed: ${e?.message || e}.`, { id: tId });
    }
  }, [accountantPhoneRaw, branchFilter]);

  const phoneDigitsByEmployeeIdLower = useMemo(() => {
    const m = new Map();
    for (const e of employees) {
      const id = String(e.employeeId || "").trim().toLowerCase();
      if (!id) continue;
      m.set(id, sanitizePhoneForWhatsApp(e.phone));
    }
    return m;
  }, [employees]);

  const phoneDigitsForStaffLine = useCallback(
    (line) => {
      const eid = String(line?.employeeId || "").trim().toLowerCase();
      if (eid) {
        const d = phoneDigitsByEmployeeIdLower.get(eid) || "";
        if (d) return d;
      }
      const name = String(line?.name || "").trim();
      if (!name) return "";
      for (const e of employees) {
        if (e.employeeName.trim() !== name) continue;
        const d = sanitizePhoneForWhatsApp(e.phone);
        if (d) return d;
      }
      return "";
    },
    [employees, phoneDigitsByEmployeeIdLower]
  );

  const phoneDigitsForPoolPerson = useCallback(
    (row) => {
      const eid = String(row?.employeeId || "").trim().toLowerCase();
      if (eid) {
        const d = phoneDigitsByEmployeeIdLower.get(eid) || "";
        if (d) return d;
      }
      const target = String(row?.name || "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
      for (const e of employees) {
        const n = String(e.employeeName || "")
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "");
        if (!n || !target) continue;
        if (n === target || n.includes(target) || target.includes(n)) {
          const d = sanitizePhoneForWhatsApp(e.phone);
          if (d) return d;
        }
      }
      return "";
    },
    [employees, phoneDigitsByEmployeeIdLower]
  );

  const copyWhatsAppText = useCallback(async (message) => {
    try {
      if (!navigator?.clipboard?.writeText) throw new Error("Clipboard is not supported");
      await navigator.clipboard.writeText(message);
      toast.success("WhatsApp message copied.");
    } catch {
      toast.error("Could not copy message.");
    }
  }, []);

  const markStaffWhatsAppSent = useCallback(
    async (line, index) => {
      const key = staffLineWhatsAppKey(line, index);
      if (!key) return;
      setWhatsappSentStaffLines((prev) => ({
        ...prev,
        [key]: prev[key] || new Date().toISOString(),
      }));
      setWhatsappMarkingKey(key);
      try {
        const result = await markStaffLineWhatsAppSent({
          year,
          monthIndex,
          branchFilter,
          lineKey: key,
        });
        setWhatsappSentStaffLines(result.whatsappSentStaffLines);
        setSaved((prev) =>
          prev
            ? { ...prev, whatsappSentStaffLines: result.whatsappSentStaffLines }
            : prev
        );
      } catch (e) {
        console.error("[monthlyIncentiveView] WhatsApp sent mark failed", e);
        toast.error(e?.message || "Could not save blue tick.");
      } finally {
        setWhatsappMarkingKey("");
      }
    },
    [year, monthIndex, branchFilter]
  );

  const brandRows = saved?.brandRows || [];
  const staffLines = saved?.staffLines || [];
  const staffPoolPctOverrides = useMemo(
    () => resolveStaffPoolPctOverridesFromSnapshot(saved),
    [saved]
  );
  const personTotals = useMemo(() => {
    const base = normalizePersonTotalsFromFirestore(saved?.personTotals);
    if (!staffPoolPctOverrides.length) return base;
    const byName = new Map(base.map((r) => [r.name, { ...r }]));
    for (const o of staffPoolPctOverrides) {
      byName.set(o.name, {
        name: o.name,
        amount: Number(o.amount) || 0,
        totalPct: Number(o.ratePct) || 0,
        staffPoolPct: true,
        othersTotal: Number(o.othersTotal) || 0,
      });
    }
    return [...byName.values()].sort((a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0));
  }, [saved?.personTotals, staffPoolPctOverrides]);
  const deductParties = saved?.deductParties || [];
  const summary = saved?.summary || {};
  const isSpareway = Boolean(saved?.isSparewayBranch);
  const sparewaySummary = saved?.sparewaySummary;

  const displayTotalFinal = useMemo(() => {
    if (personTotals.length) {
      return personTotals.reduce((a, r) => a + (Number(r.amount) || 0), 0);
    }
    return Number(summary.totalFinal) || 0;
  }, [personTotals, summary.totalFinal]);

  const oePoolTotal = useMemo(() => {
    if (summary.oePoolTotal != null) return Number(summary.oePoolTotal) || 0;
    if (isSpareway && sparewaySummary?.oePool != null) return Number(sparewaySummary.oePool) || 0;
    return brandRows.reduce((s, r) => s + (Number(r.oeIncentive) || 0), 0);
  }, [summary.oePoolTotal, brandRows, isSpareway, sparewaySummary?.oePool]);

  const ordPoolTotal = useMemo(() => {
    if (summary.ordPoolTotal != null) return Number(summary.ordPoolTotal) || 0;
    if (isSpareway && sparewaySummary?.ordPool != null) return Number(sparewaySummary.ordPool) || 0;
    return brandRows.reduce((s, r) => s + (Number(r.ordIncentive) || 0), 0);
  }, [summary.ordPoolTotal, brandRows, isSpareway, sparewaySummary?.ordPool]);

  const staffAllocationImageFileName = useMemo(
    () =>
      `monthly-incentive-staff-allocation-${year}-${String(monthIndex + 1).padStart(2, "0")}-${branchFilter
        .replace(/\s+/g, "-")
        .toLowerCase()}.png`,
    [branchFilter, monthIndex, year]
  );

  useEffect(() => {
    return () => {
      if (staffAllocationImageUrl) URL.revokeObjectURL(staffAllocationImageUrl);
    };
  }, [staffAllocationImageUrl]);

  const renderStaffAllocationImage = useCallback(async () => {
    const el = staffAllocationImageCardRef.current;
    if (!el) {
      toast.error("Image template is not ready.");
      return null;
    }
    try {
      setStaffAllocationImageBusy(true);
      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: Math.max(2, window.devicePixelRatio || 1),
        useCORS: true,
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
      if (!blob) throw new Error("Could not generate PNG");
      return blob;
    } catch (e) {
      toast.error(e?.message || "Could not generate image.");
      return null;
    } finally {
      setStaffAllocationImageBusy(false);
    }
  }, []);

  const ensureStaffAllocationImageUrl = useCallback(async () => {
    const blob = await renderStaffAllocationImage();
    if (!blob) return "";
    const nextUrl = URL.createObjectURL(blob);
    setStaffAllocationImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return nextUrl;
    });
    return nextUrl;
  }, [renderStaffAllocationImage]);

  const downloadStaffAllocationImage = useCallback(async () => {
    const url = staffAllocationImageUrl || (await ensureStaffAllocationImageUrl());
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = staffAllocationImageFileName;
    a.click();
    toast.success("Staff allocation image downloaded.");
  }, [staffAllocationImageFileName, staffAllocationImageUrl, ensureStaffAllocationImageUrl]);

  const copyStaffAllocationImage = useCallback(async () => {
    try {
      if (!navigator?.clipboard?.write || typeof window.ClipboardItem === "undefined") {
        throw new Error("Image copy is not supported in this browser");
      }
      const blob = await renderStaffAllocationImage();
      if (!blob) return;
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      toast.success("Staff allocation image copied.");
    } catch (e) {
      toast.error(e?.message || "Could not copy image.");
    }
  }, [renderStaffAllocationImage]);

  const openWhatsAppForStaffAllocationImage = useCallback(async () => {
    await downloadStaffAllocationImage();
    const msg = "Monthly incentive staff allocation image is ready. Please find attached image.";
    window.open(whatsAppSendUrl(accountantPhoneDigits, msg), "_blank", "noopener,noreferrer");
    toast("WhatsApp opened. Attach the downloaded image and send.", { icon: "🖼️" });
  }, [accountantPhoneDigits, downloadStaffAllocationImage]);

  return (
    <>
      <MenuBar />
      <HeaderBar />

      <main className="msr-upload-page msr-upload-page--full msr-no-print">
        <header className="msr-upload-page-head">
          <div>
            <p className="msr-upload-page-kicker">Incentives</p>
            <h1 className="msr-upload-page-title">Monthly incentive — saved snapshot</h1>
            <p className="msr-upload-page-sub">
              Read-only view of incentive totals uploaded from the calculation page. Select month and
              branch to load the saved snapshot from Firestore.
            </p>
          </div>
          <Link to={editHref} className="msr-upload-page-back">
            Open calculation page →
          </Link>
        </header>

        <div className="msr-toolbar msr-upload-toolbar">
          <label className="msr-toolbar-label">
            Financial year{" "}
            <select
              className="msr-toolbar-select"
              value={fyStartYear}
              onChange={(e) => setFyStartYear(Number(e.target.value))}
            >
              {fySelectOptions.map(({ startYear, label }) => (
                <option key={startYear} value={startYear}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="msr-toolbar-label">
            Month{" "}
            <select
              className="msr-toolbar-select"
              value={fySlot}
              onChange={(e) => setFySlot(Number(e.target.value))}
            >
              {fyMonthSequence.map((s) => (
                <option key={`${s.calendarYear}-${s.monthIndex}`} value={s.fySlot}>
                  {MONTH_OPTIONS[s.monthIndex]} {s.calendarYear}
                </option>
              ))}
            </select>
          </label>
          <span className="msr-toolbar-fy-hint">{fyLabel} · April–March</span>
          <label className="msr-toolbar-label">
            Branch{" "}
            <select
              className="msr-toolbar-select"
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
            >
              {BRANCH_OPTIONS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading ? <p className="msr-toolbar-status">Loading saved snapshot…</p> : null}

        {!loading && !saved ? (
          <p className="msr-empty-msg">
            No saved incentive for <strong>{periodLabel}</strong> · <strong>{branchLabel}</strong>.
            Open the calculation page, finalize staff and deductions, then click{" "}
            <strong>Save to Firestore</strong>.
          </p>
        ) : null}

        {saved ? (
          <>
            <section className="msr-precalc-panel">
              <h2 className="msr-precalc-title">
                {periodLabel} · {branchLabel}
              </h2>
              {saved.generatedAt ? (
                <p className="msr-precalc-meta">
                  Saved: {new Date(saved.generatedAt).toLocaleString()}
                  {saved.generatedBy ? ` · ${saved.generatedBy}` : ""}
                  {saved.fyLabel ? ` · FY ${saved.fyLabel}` : ""}
                </p>
              ) : null}
              {deductParties.length ? (
                <p className="msr-precalc-meta">
                  Customer deductions:{" "}
                  {deductParties.map((p) => p.partyName || p.name).filter(Boolean).join(", ")}
                </p>
              ) : null}
              <div className="msr-precalc-totals-grid">
                <div className="msr-precalc-total-box msr-bp-box-total">
                  <span>Brand pool</span>
                  <strong>{fmt(summary.grandTotal ?? oePoolTotal + ordPoolTotal)}</strong>
                </div>
                <div className="msr-precalc-total-box msr-bp-box-ord">
                  <span>Ordinary pool</span>
                  <strong>{fmt(ordPoolTotal)}</strong>
                </div>
                <div className="msr-precalc-total-box msr-bp-box-oe">
                  <span>OE pool</span>
                  <strong>{fmt(oePoolTotal)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Staff gross</span>
                  <strong>{fmt(summary.totalGross)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Leave cuts</span>
                  <strong>{fmt(summary.totalLeaveCuts ?? summary.totalCuts)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Late coming cut</span>
                  <strong>{fmt(summary.totalCutAmount)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Net pay (staff)</span>
                  <strong>{fmt(displayTotalFinal)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Unassigned</span>
                  <strong>{fmt(summary.totalUnassigned)}</strong>
                </div>
                {summary.partyDedOe != null || summary.partyDedOrd != null ? (
                  <>
                    <div className="msr-precalc-total-box">
                      <span>Party OE deducted</span>
                      <strong>{fmt(summary.partyDedOe)}</strong>
                    </div>
                    <div className="msr-precalc-total-box">
                      <span>Party ord deducted</span>
                      <strong>{fmt(summary.partyDedOrd)}</strong>
                    </div>
                  </>
                ) : null}
              </div>
            </section>

            {isSpareway && sparewaySummary ? (
              <section className="msr-precalc-panel">
                <h2 className="msr-precalc-title">Spareway OE / Ordinary</h2>
                <div className="msr-precalc-totals-grid">
                  <div className="msr-precalc-total-box">
                    <span>Total OE sale</span>
                    <strong>{fmt(sparewaySummary.oeSalesTotal)}</strong>
                  </div>
                  <div className="msr-precalc-total-box">
                    <span>OE eligible</span>
                    <strong>{fmt(sparewaySummary.oeEligible)}</strong>
                  </div>
                  <div className="msr-precalc-total-box">
                    <span>OE pool</span>
                    <strong>{fmt(sparewaySummary.oePool)}</strong>
                  </div>
                  <div className="msr-precalc-total-box">
                    <span>Total ord sale</span>
                    <strong>{fmt(sparewaySummary.ordSalesTotal)}</strong>
                  </div>
                  <div className="msr-precalc-total-box">
                    <span>Ord eligible</span>
                    <strong>{fmt(sparewaySummary.ordEligible)}</strong>
                  </div>
                  <div className="msr-precalc-total-box">
                    <span>Ord pool</span>
                    <strong>{fmt(sparewaySummary.ordPool)}</strong>
                  </div>
                </div>
              </section>
            ) : null}

            {brandRows.length ? (
              <section className="msr-precalc-panel">
                <h2 className="msr-precalc-title">Brand pool</h2>
                <div className="msr-precalc-table-wrap">
                  {isSpareway ? (
                    <table className="msr-precalc-table msr-incentive-brand-pool">
                      <thead>
                        <tr>
                          <th>Brand</th>
                          <th className="msr-num">Brand total</th>
                          <th className="msr-num msr-bp-ord-th">Ord sale</th>
                          <th className="msr-num msr-bp-oe-th">OE sale</th>
                          <th className="msr-num msr-bp-ord-incentive">Ord incentive</th>
                          <th className="msr-num msr-bp-oe-incentive">OE incentive</th>
                          <th className="msr-num msr-bp-total-th">Total incentive</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brandRows.map((r) => (
                          <tr key={r.brandId}>
                            <td>{r.brandId}</td>
                            <td className="msr-num">{fmt(r.brandTotal)}</td>
                            <td className="msr-num msr-bp-ord-cell">{fmt(r.ordSale)}</td>
                            <td className="msr-num msr-bp-oe-cell">{fmt(r.oeSale)}</td>
                            <td className="msr-num msr-bp-ord-incentive">{fmt(r.ordIncentive)}</td>
                            <td className="msr-num msr-bp-oe-incentive">{fmt(r.oeIncentive)}</td>
                            <td className="msr-num msr-bp-total-cell">{fmt(r.section)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>Total</td>
                          <td className="msr-num" />
                          <td className="msr-num msr-bp-ord-cell">
                            {fmt(brandRows.reduce((s, r) => s + (Number(r.ordSale) || 0), 0))}
                          </td>
                          <td className="msr-num msr-bp-oe-cell">
                            {fmt(brandRows.reduce((s, r) => s + (Number(r.oeSale) || 0), 0))}
                          </td>
                          <td className="msr-num msr-bp-foot-ord">{fmt(ordPoolTotal)}</td>
                          <td className="msr-num msr-bp-foot-oe">{fmt(oePoolTotal)}</td>
                          <td className="msr-num msr-bp-foot-total">
                            {fmt(summary.grandTotal ?? oePoolTotal + ordPoolTotal)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  ) : (
                  <table className="msr-precalc-table msr-incentive-brand-pool">
                    <thead>
                      <tr>
                        <th rowSpan={2}>Brand</th>
                        <th colSpan={5} className="msr-bp-ord-group">
                          Ordinary
                        </th>
                        <th colSpan={4} className="msr-bp-oe-group">
                          OE
                        </th>
                        <th rowSpan={2} className="msr-num msr-bp-total-th">
                          Total incentive
                        </th>
                      </tr>
                      <tr>
                        <th className="msr-num msr-bp-ord-th">Ord sale</th>
                        <th className="msr-num msr-bp-ord-th">Party ord</th>
                        <th className="msr-num msr-bp-ord-th">Net ord</th>
                        <th className="msr-num msr-bp-ord-th" title="Blank-brand / miscellaneous ordinary ÷ 8">
                          Misc ÷8
                        </th>
                        <th className="msr-num msr-bp-ord-incentive">Ord incentive</th>
                        <th className="msr-num msr-bp-oe-th">OE sale</th>
                        <th className="msr-num msr-bp-oe-th">Party OE</th>
                        <th className="msr-num msr-bp-oe-th">Net OE</th>
                        <th className="msr-num msr-bp-oe-incentive">OE incentive</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brandRows.map((r) => (
                        <tr key={r.brandId}>
                          <td>{r.brandId}</td>
                          <td className="msr-num msr-bp-ord-cell">{fmt(r.grossOrd ?? r.ordSale)}</td>
                          <td className="msr-num msr-bp-ord-cell">{r.partyOrd > 0 ? fmt(-r.partyOrd) : "—"}</td>
                          <td className="msr-num msr-bp-ord-cell">{fmt(r.ordSale)}</td>
                          <td className="msr-num msr-bp-ord-cell">
                            {r.miscShare > 0 ? fmt(r.miscShare) : "—"}
                          </td>
                          <td className="msr-num msr-bp-ord-incentive">{fmt(r.ordIncentive)}</td>
                          <td className="msr-num msr-bp-oe-cell">{fmt(r.grossOe ?? r.oeSale)}</td>
                          <td className="msr-num msr-bp-oe-cell">{r.partyOe > 0 ? fmt(-r.partyOe) : "—"}</td>
                          <td className="msr-num msr-bp-oe-cell">{fmt(r.oeSale)}</td>
                          <td className="msr-num msr-bp-oe-incentive">{fmt(r.oeIncentive)}</td>
                          <td className="msr-num msr-bp-total-cell">{fmt(r.section)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td colSpan={3} className="msr-bp-ord-cell" style={{ textAlign: "right", fontWeight: 600 }}>
                          Ordinary pool
                        </td>
                        <td className="msr-num msr-bp-ord-cell">
                          {brandRows.some((r) => (Number(r.miscShare) || 0) > 0)
                            ? fmt(brandRows.reduce((s, r) => s + (Number(r.miscShare) || 0), 0))
                            : "—"}
                        </td>
                        <td className="msr-num msr-bp-foot-ord">{fmt(ordPoolTotal)}</td>
                        <td colSpan={3} className="msr-bp-oe-cell" style={{ textAlign: "right", fontWeight: 600 }}>
                          OE pool
                        </td>
                        <td className="msr-num msr-bp-foot-oe">{fmt(oePoolTotal)}</td>
                        <td className="msr-num msr-bp-foot-total">
                          {fmt(summary.grandTotal ?? oePoolTotal + ordPoolTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  )}
                </div>
              </section>
            ) : null}

            {staffLines.length || staffPoolPctOverrides.length ? (
              <section className="msr-precalc-panel relative">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                  <div>
                    <h2 className="msr-precalc-title" style={{ marginBottom: "0.35rem" }}>
                      Staff allocation
                    </h2>
                    <p className="msr-precalc-meta" style={{ margin: 0 }}>
                      Per-row <strong>Send</strong> goes to the employee when <strong>Employee ID</strong> is set.
                      Use <strong>Send</strong> on each row to open WhatsApp — a blue tick appears after you click
                      (saved on this snapshot). Use <strong>Send image</strong> for the full allocation table (PNG
                      download + WhatsApp).
                      {employeesLoading ? " Loading employee directory…" : ""}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 msr-no-print sm:min-w-[22rem]">
                    <div className="flex items-end gap-2">
                      <label className="text-xs flex-1">
                        <span className="block text-slate-500 mb-0.5 font-medium uppercase tracking-wide">
                          Accountant WhatsApp no.
                        </span>
                        <input
                          value={accountantPhoneRaw}
                          onChange={(e) => setAccountantPhoneRaw(e.target.value)}
                          placeholder="e.g. 9876543210"
                          className="w-full border border-slate-300 rounded-lg px-2.5 py-2 bg-white text-sm shadow-sm"
                          inputMode="tel"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={saveAccountantPhone}
                        className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
                      >
                        Save
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={copyStaffAllocationImage}
                        disabled={staffAllocationImageBusy}
                        className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
                      >
                        {staffAllocationImageBusy ? "Preparing…" : "Copy image"}
                      </button>
                      <button
                        type="button"
                        onClick={downloadStaffAllocationImage}
                        disabled={staffAllocationImageBusy}
                        className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
                      >
                        Download image
                      </button>
                      <button
                        type="button"
                        onClick={openWhatsAppForStaffAllocationImage}
                        disabled={staffAllocationImageBusy}
                        title={
                          accountantPhoneDigits
                            ? `Send image to ${accountantPhoneDigits}`
                            : "Choose contact in WhatsApp"
                        }
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#20bd5a] disabled:opacity-60"
                      >
                        <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <path d={WHATSAPP_ICON_PATH} />
                        </svg>
                        {staffAllocationImageBusy ? "Preparing…" : "Send image"}
                      </button>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Image format only: download or copy the PNG, then attach it in WhatsApp.
                    </p>
                  </div>
                </div>
                <div className="absolute -left-[99999px] top-0 pointer-events-none opacity-0" aria-hidden>
                  <div
                    ref={staffAllocationImageCardRef}
                    style={{
                      width: "1180px",
                      background: "#ffffff",
                      color: "#0f172a",
                      border: "1px solid #e2e8f0",
                      borderRadius: "14px",
                      padding: "24px",
                      fontFamily:
                        'Arial, Helvetica, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    }}
                  >
                    <div style={{ fontSize: "22px", fontWeight: 700, marginBottom: "8px" }}>
                      Monthly Sales Incentive — Staff Allocation
                    </div>
                    <div style={{ fontSize: "13px", color: "#334155", marginBottom: "16px" }}>
                      {periodLabel} | {branchLabel} | FY {fyLabel}
                    </div>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "12px",
                      }}
                    >
                      <thead>
                        <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                          {[
                            "Name",
                            "Brand",
                            "Leave",
                            "Gross",
                            "Leave cut",
                            "Late cut",
                            "Branding share",
                            "Final",
                          ].map((h, hi) => (
                            <th
                              key={h}
                              style={{
                                padding: "8px 6px",
                                textAlign: hi === 0 || hi === 1 ? "left" : "right",
                                fontWeight: 600,
                                color: "#475569",
                                textTransform: "uppercase",
                                fontSize: "10px",
                                letterSpacing: "0.03em",
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {staffLines.map((L, i) => {
                          const { leaveCut, cutAmount } = parseStaffLineCuts(L);
                          const cells = [
                            L.name,
                            L.brandId || "—",
                            L.leave ?? "—",
                            fmt(L.base),
                            fmt(leaveCut),
                            fmt(cutAmount),
                            fmt(L.poolShare),
                            fmt(L.finalAmt),
                          ];
                          return (
                            <tr
                              key={`img-${L.name}-${L.brandId}-${i}`}
                              style={{
                                borderBottom: "1px solid #f1f5f9",
                                background: i % 2 === 0 ? "#ffffff" : "#fafbfc",
                              }}
                            >
                              {cells.map((cell, ci) => (
                                <td
                                  key={ci}
                                  style={{
                                    padding: "7px 6px",
                                    textAlign: ci <= 1 ? "left" : "right",
                                    fontWeight: ci === 0 ? 600 : 400,
                                    whiteSpace: ci === 0 ? "nowrap" : undefined,
                                  }}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                        {staffPoolPctOverrides.map((o, i) => {
                          const cells = [
                            o.name,
                            `${fmtPct(o.ratePct)} of staff`,
                            "—",
                            fmt(o.othersTotal),
                            "—",
                            "—",
                            "—",
                            fmt(o.amount),
                          ];
                          return (
                            <tr
                              key={`img-pool-${o.name}`}
                              style={{
                                borderBottom: "1px solid #fef3c7",
                                background: "#fffbeb",
                              }}
                            >
                              {cells.map((cell, ci) => (
                                <td
                                  key={ci}
                                  style={{
                                    padding: "7px 6px",
                                    textAlign: ci <= 1 ? "left" : "right",
                                    fontWeight: ci === 0 || ci === 7 ? 700 : 400,
                                    whiteSpace: ci === 0 ? "nowrap" : undefined,
                                  }}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: "2px solid #e2e8f0", background: "#f8fafc" }}>
                          <td
                            colSpan={7}
                            style={{ padding: "10px 6px", textAlign: "right", fontWeight: 700 }}
                          >
                            Total net pay
                          </td>
                          <td style={{ padding: "10px 6px", textAlign: "right", fontWeight: 700 }}>
                            {fmt(displayTotalFinal)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                    <div style={{ marginTop: "10px", fontSize: "11px", color: "#64748b" }}>
                      Generated on {new Date().toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
                <div className="msr-precalc-table-wrap">
                  {staffLines.length ? (
                  <table className="msr-precalc-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Brand</th>
                        <th className="msr-num">%</th>
                        <th className="msr-num">Leave</th>
                        <th className="msr-num">Gross</th>
                        <th className="msr-num">Leave cut</th>
                        <th className="msr-num">Late coming cut amount</th>
                        <th className="msr-num">Branding share</th>
                        <th className="msr-num">Final</th>
                        <th className="msr-num msr-no-print">WhatsApp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staffLines.map((L, i) => {
                        const { leaveCut, cutAmount } = parseStaffLineCuts(L);
                        const digits = phoneDigitsForStaffLine(L);
                        const msg = buildStaffLineWhatsAppText({
                          personName: L.name,
                          brandId: L.brandId,
                          autoCut: leaveCut,
                          manualCut: cutAmount,
                          finalAmt: L.finalAmt,
                          periodLabel,
                          branchLabel,
                          fyLabel,
                          fmt,
                        });
                        const href = whatsAppSendUrl(digits, msg);
                        const waKey = staffLineWhatsAppKey(L, i);
                        const sentAt = whatsappSentStaffLines[waKey];
                        const isSent = Boolean(sentAt);
                        const isMarking = whatsappMarkingKey === waKey;
                        return (
                          <tr key={`${L.name}-${L.brandId}-${i}`}>
                            <td>{L.name}</td>
                            <td>{L.brandId || "—"}</td>
                            <td className="msr-num">{L.pct}</td>
                            <td className="msr-num">{L.leave}</td>
                            <td className="msr-num">{fmt(L.base)}</td>
                            <td className="msr-num">{fmt(leaveCut)}</td>
                            <td className="msr-num">{fmt(cutAmount)}</td>
                            <td className="msr-num">{fmt(L.poolShare)}</td>
                            <td className="msr-num">{fmt(L.finalAmt)}</td>
                            <td className="msr-num msr-no-print">
                              <div className="inline-flex items-center justify-end gap-1.5 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => copyWhatsAppText(msg)}
                                  className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                                >
                                  Copy
                                </button>
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={
                                    isSent
                                      ? `Sent ${new Date(sentAt).toLocaleString("en-IN")}`
                                      : digits
                                        ? `Send to ${digits}`
                                        : "Choose contact in WhatsApp"
                                  }
                                  onClick={() => {
                                    void markStaffWhatsAppSent(L, i);
                                  }}
                                  className={`inline-flex items-center justify-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-white shadow-sm ${
                                    isSent
                                      ? "bg-[#128C7E] hover:bg-[#0e7a6d]"
                                      : "bg-[#25D366] hover:bg-[#20bd5a]"
                                  }`}
                                >
                                  <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                                    <path d={WHATSAPP_ICON_PATH} />
                                  </svg>
                                  {isMarking ? "…" : "Send"}
                                </a>
                                {isSent ? (
                                  <span
                                    className="inline-flex items-center rounded-md border border-[#53bdeb]/40 bg-[#e7f8ff] px-1.5 py-1"
                                    title={`WhatsApp sent ${new Date(sentAt).toLocaleString("en-IN")}`}
                                  >
                                    <svg
                                      className="h-3.5 w-3.5 shrink-0"
                                      viewBox="0 0 16 11"
                                      fill="none"
                                      aria-label="WhatsApp sent"
                                      role="img"
                                    >
                                      {WHATSAPP_BLUE_TICK_PATHS.map((d) => (
                                        <path key={d} d={d} fill="#53bdeb" />
                                      ))}
                                    </svg>
                                  </span>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={8} style={{ textAlign: "right", fontWeight: 600 }}>
                          Staff brand lines
                        </td>
                        <td className="msr-num" style={{ fontWeight: 700 }}>
                          {fmt(staffLines.reduce((a, L) => a + (Number(L.finalAmt) || 0), 0))}
                        </td>
                        <td className="msr-no-print" />
                      </tr>
                    </tfoot>
                  </table>
                  ) : null}

                  {staffPoolPctOverrides.length ? (
                    <div className={staffLines.length ? "mt-4" : undefined}>
                      <h3 className="text-sm font-semibold text-amber-950 mb-1">
                        Staff pool % incentive
                      </h3>
                      <p className="text-xs text-slate-500 mb-2">
                        Fixed % of all other staff net pay (e.g. VINOD.MENJERI @ 5%). Not a brand allocation row.
                      </p>
                      <table className="msr-precalc-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th className="msr-num">Rate</th>
                            <th className="msr-num">Other staff total</th>
                            <th className="msr-num">Incentive</th>
                            <th className="msr-num msr-no-print">WhatsApp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {staffPoolPctOverrides.map((o) => {
                            const digits = phoneDigitsForPoolPerson(o);
                            const msg = buildStaffPoolPctWhatsAppText({
                              personName: o.name,
                              ratePct: o.ratePct,
                              othersTotal: o.othersTotal,
                              amount: o.amount,
                              periodLabel,
                              branchLabel,
                              fyLabel,
                              fmt,
                              fmtPct,
                            });
                            const href = whatsAppSendUrl(digits, msg);
                            const waLine = {
                              name: o.name,
                              employeeId: o.employeeId,
                              brandId: "STAFF_POOL",
                              index: 0,
                            };
                            const waKey = staffLineWhatsAppKey(waLine, 0);
                            const sentAt = whatsappSentStaffLines[waKey];
                            const isSent = Boolean(sentAt);
                            const isMarking = whatsappMarkingKey === waKey;
                            return (
                              <tr key={`pool-${o.name}`}>
                                <td>
                                  {o.name}
                                  <span className="msr-muted"> ({fmtPct(o.ratePct)} of other staff)</span>
                                </td>
                                <td className="msr-num">{fmtPct(o.ratePct)}</td>
                                <td className="msr-num">{fmt(o.othersTotal)}</td>
                                <td className="msr-num" style={{ fontWeight: 700 }}>
                                  {fmt(o.amount)}
                                </td>
                                <td className="msr-num msr-no-print">
                                  <div className="inline-flex items-center justify-end gap-1.5 flex-wrap">
                                    <button
                                      type="button"
                                      onClick={() => copyWhatsAppText(msg)}
                                      className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                                    >
                                      Copy
                                    </button>
                                    <a
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={
                                        isSent
                                          ? `Sent ${new Date(sentAt).toLocaleString("en-IN")}`
                                          : digits
                                            ? `Send to ${digits}`
                                            : "Choose contact in WhatsApp"
                                      }
                                      onClick={() => {
                                        void markStaffWhatsAppSent(waLine, 0);
                                      }}
                                      className={`inline-flex items-center justify-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-white shadow-sm ${
                                        isSent
                                          ? "bg-[#128C7E] hover:bg-[#0e7a6d]"
                                          : "bg-[#25D366] hover:bg-[#20bd5a]"
                                      }`}
                                    >
                                      <svg
                                        className="h-3.5 w-3.5 shrink-0"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        aria-hidden
                                      >
                                        <path d={WHATSAPP_ICON_PATH} />
                                      </svg>
                                      {isMarking ? "…" : "Send"}
                                    </a>
                                    {isSent ? (
                                      <span
                                        className="inline-flex items-center rounded-md border border-[#53bdeb]/40 bg-[#e7f8ff] px-1.5 py-1"
                                        title={`WhatsApp sent ${new Date(sentAt).toLocaleString("en-IN")}`}
                                      >
                                        <svg
                                          className="h-3.5 w-3.5 shrink-0"
                                          viewBox="0 0 16 11"
                                          fill="none"
                                          aria-label="WhatsApp sent"
                                          role="img"
                                        >
                                          {WHATSAPP_BLUE_TICK_PATHS.map((d) => (
                                            <path key={d} d={d} fill="#53bdeb" />
                                          ))}
                                        </svg>
                                      </span>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={3} style={{ textAlign: "right", fontWeight: 600 }}>
                              Total net pay (all)
                            </td>
                            <td className="msr-num" style={{ fontWeight: 700 }}>
                              {fmt(displayTotalFinal)}
                            </td>
                            <td className="msr-no-print" />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            {personTotals.length ? (
              <section className="msr-precalc-panel">
                <h2 className="msr-precalc-title">Net pay by name</h2>
                <div className="msr-precalc-table-wrap">
                  <table className="msr-precalc-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th className="msr-num">Total %</th>
                        <th className="msr-num">Net pay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {personTotals.map((row) => (
                        <tr key={row.name}>
                          <td>
                            {row.name}
                            {row.staffPoolPct ? (
                              <span className="msr-muted"> ({fmtPct(row.totalPct)} of other staff)</span>
                            ) : null}
                          </td>
                          <td className="msr-num">{fmtPct(row.totalPct)}</td>
                          <td className="msr-num">{fmt(row.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}
          </>
        ) : null}
      </main>
    </>
  );
}
